import { useMemo } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSIconButton from "views/system-design/DSIconButton";
import DSTooltip from "views/system-design/DSTooltip";
import CloseIcon from "@mui/icons-material/Close";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { REPO_LABELS, LABEL_COLORS, STATUS_COLORS, NO_LABEL } from "../types";
import { LabelStats } from "api/tickets";

const PANEL_WIDTH = 300;

type LabelPanelProps = {
  labelStats: LabelStats[];
  unlabeledCount: number;
  selectedLabels: string[];
  onToggleLabel: (label: string) => void;
  onClose: () => void;
};

// The panel only tracks the repo labels, in their canonical order.
function orderRepoLabels(labelStats: LabelStats[]): LabelStats[] {
  const statsByLabel = new Map(labelStats.map((stats) => [stats.label, stats]));
  return REPO_LABELS.map((label) => statsByLabel.get(label)).filter(
    (stats): stats is LabelStats => !!stats && stats.total > 0,
  );
}

function ProgressBar({ stats }: { stats: LabelStats }) {
  if (stats.total === 0) return null;

  const donePercent = (stats.done / stats.total) * 100;
  const activePercent = (stats.inProgress / stats.total) * 100;

  return (
    <DSTooltip
      title={`Done: ${stats.done} of ${stats.total} (work items)\nBacklog: ${stats.backlog}`}
    >
      <DSBox
        sx={{
          width: "100%",
          height: 5,
          bgcolor: "white.16",
          borderRadius: 2,
          overflow: "hidden",
          display: "flex",
        }}
      >
        <DSBox
          sx={{
            width: `${donePercent}%`,
            bgcolor: STATUS_COLORS.done,
            transition: "width 0.3s",
          }}
        />
        <DSBox
          sx={{
            width: `${activePercent}%`,
            bgcolor: STATUS_COLORS.in_progress,
            transition: "width 0.3s",
          }}
        />
      </DSBox>
    </DSTooltip>
  );
}

export default function LabelPanel({
  labelStats,
  unlabeledCount,
  selectedLabels,
  onToggleLabel,
  onClose,
}: LabelPanelProps) {
  const repoLabelStats = useMemo(
    () => orderRepoLabels(labelStats),
    [labelStats],
  );

  return (
    <DSBox
      sx={{
        width: PANEL_WIDTH,
        minWidth: PANEL_WIDTH,
        height: "fit-content",
        bgcolor: "background.paper",
        borderRadius: 2,
        mr: 2,
        border: 1,
        borderColor: "divider",
        boxShadow: 2,
        display: "flex",
        flexDirection: "column",
        alignSelf: "flex-start",
      }}
    >
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <DSTypography
          variant="subtitle2"
          sx={{
            fontWeight: 800,
            color: "text.secondary",
            textTransform: "uppercase",
            fontSize: 15,
          }}
        >
          Label
        </DSTypography>
        <DSIconButton
          size="small"
          onClick={onClose}
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon fontSize="small" />
        </DSIconButton>
      </DSBox>

      <DSBox
        onClick={() => onToggleLabel(NO_LABEL)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: "pointer",
          bgcolor: selectedLabels.includes(NO_LABEL)
            ? "action.selected"
            : "transparent",
          "&:hover": { bgcolor: "action.hover" },
          transition: "background-color 0.15s",
        }}
      >
        {selectedLabels.includes(NO_LABEL) ? (
          <CheckBoxIcon sx={{ fontSize: 20, color: "text.disabled" }} />
        ) : (
          <CheckBoxOutlineBlankIcon
            sx={{ fontSize: 20, color: "text.disabled" }}
          />
        )}
        <DSTypography
          variant="body2"
          sx={{ color: "text.primary", fontSize: 14 }}
        >
          No label
        </DSTypography>
        <DSTypography
          variant="caption"
          sx={{ color: "text.disabled", ml: "auto", fontSize: 13 }}
        >
          {unlabeledCount}
        </DSTypography>
      </DSBox>

      {repoLabelStats.map((stats) => {
        const isSelected = selectedLabels.includes(stats.label);
        const color = LABEL_COLORS[stats.label] ?? "grey.500";
        const boardItems = stats.total - stats.backlog;

        return (
          <DSBox
            key={stats.label}
            onClick={() => onToggleLabel(stats.label)}
            sx={{
              px: 2,
              py: 1.5,
              cursor: "pointer",
              bgcolor: isSelected ? "action.selected" : "transparent",
              "&:hover": { bgcolor: "action.hover" },
              transition: "background-color 0.15s",
            }}
          >
            <DSTooltip
              title={`Done: ${stats.done} of ${boardItems} (work items)\nBacklog: ${stats.backlog}`}
            >
              <DSBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mb: 0.75,
                }}
              >
                <DSBox
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: color,
                    flexShrink: 0,
                  }}
                />
                <DSTypography
                  variant="body2"
                  sx={{
                    color: "text.primary",
                    fontSize: 14,
                    fontWeight: isSelected ? 600 : 400,
                    textTransform: "capitalize",
                  }}
                >
                  {stats.label}
                </DSTypography>
                <DSTypography
                  variant="caption"
                  sx={{ color: "text.disabled", ml: "auto", fontSize: 13 }}
                >
                  {stats.total}
                </DSTypography>
              </DSBox>
            </DSTooltip>
            <ProgressBar stats={stats} />
          </DSBox>
        );
      })}
    </DSBox>
  );
}
